import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StorageService } from '@cbuild/cs-common-data-services';
import {
  Feature,
  Module,
  PermissionComponent,
  SiteVisitFeaturePermissionMask
} from '@cbuild/shared';
import { filter, Subject, switchMap, take, takeUntil } from 'rxjs';
import { UserInfo } from '../models/user-info.model';
import { UserOrganization } from '../models/user-organization';
import { ApplicationService } from '../services/application.service';
import { AuthenticationService } from '../services/authentication/authentication.service';
import { BaseUrlService } from '../services/base-url.service';
import { PropertyFilter } from '../services/property-filter';
import { NavMenuService } from './nav-menu.service';
import { AIAssistantPermissionMask, OrganizationConfigurationEnum } from '../../shared';

@Component({
  selector: 'cs-nav-menu',
  standalone: false,
  templateUrl: './nav-menu.component.html',
  styleUrls: ['./nav-menu.component.scss']
})
export class NavMenuComponent extends PermissionComponent
  implements OnInit, OnDestroy {
  private _onDestroy$ = new Subject();
  public userInfo: UserInfo;
  public module = Module;
  public feature = Feature;
  public siteVisitFeaturePermissionMask = SiteVisitFeaturePermissionMask;
  public selectedOrg: UserOrganization;
  public userType = this.authenticationService.getUser()!.type;
  public hasProjects: 'none' | 'there';
  public hasMultipleOrg = false;
  public showAiAssistant = false;
  public orgConfigEnum = OrganizationConfigurationEnum;
  public showAIAssistant: boolean = false;
  public settingFeaturePermission = AIAssistantPermissionMask;

  @Output()
  public initialized = new EventEmitter<void>();

  constructor(
    private storageService: StorageService,
    private applicationService: ApplicationService,
    authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    private navMenuService: NavMenuService,
    private baseUrlService: BaseUrlService
  ) {
    super(authenticationService);
    const res = this.storageService.getItem<PropertyFilter>('gp_filter');
    this.applicationService.updatePropertyFilterObservable(
      res || { companyId: null, propertyId: null }
    );
  }

  public ngOnInit(): void {
    this.navMenuService
      .getConfigSettings(this.orgConfigEnum.AIAssistant)
      .pipe(take(1))
      .subscribe(res => {
        this.showAIAssistant = res.boolean;
      });
    this.listenUserChangesInAuthentication();
    this.listenBasicInfo(); // Depends on listenUserChangesInAuthentication
    this.listenCompanyUpdates(); // Depends on listenBasicInfo
    this.listenForMultipleOrg();
  }

  public listenBasicInfo(): void {
    this.applicationService.$userInfo
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(info => {
        this.userInfo = info;
        this.applicationService.projectUpdates$.next();
      });
  }

  public listenUserChangesInAuthentication(): void {
    this.authenticationService.user$
      .pipe(
        filter(_ => !!_),
        takeUntil(this._onDestroy$)
      )
      .subscribe(_ => {
        this.initialized.emit();
        if (
          this.authenticationService.isAuthenticated &&
          this.authenticationService.isOrgSelected
        ) {
          this.applicationService.loadBasicInfo();
        }
      });
  }

  public listenCompanyUpdates(): void {
    this.applicationService.projectUpdates$
      .pipe(
        switchMap(_ => this.navMenuService.getUserCompanyInfo()),
        takeUntil(this._onDestroy$)
      )
      .subscribe(res => {
        this.applicationService.companiesList = res;
        this.updateHasProjects();
      });
  }

  public listenForMultipleOrg(): void {
    this.authenticationService.$hasMultipleOrg
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(_ => {
        this.hasMultipleOrg = _;
        this.navMenuService
          .selectedOrganizationGet(this.authenticationService.orgId)
          .pipe(take(1))
          .subscribe(res => {
            this.selectedOrg = res;
          });
      });
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(0);
    this._onDestroy$.complete();
    this.applicationService.resetState();
  }

  public navigateToSettings(): void {
    this.router.navigate(['/settings'], { relativeTo: this.route });
  }

  public navigateToSelectOrg(): void {
    this.authenticationService.navigateToSelectOrg();
  }

  public applyPropertyFilter(propertyFilter: PropertyFilter): void {
    const { propertyId, companyId, propertyTypeId } = propertyFilter;
    if (propertyId === null && companyId === null) {
      this.applicationService.setPropertyFilter({
        companyId,
        propertyId,
        propertyTypeId
      });
    } else if (propertyId && this.applicationService.hasProject(propertyId)) {
      this.applicationService.setPropertyFilter({
        propertyId,
        companyId: null,
        propertyTypeId
      });
    } else if (companyId && this.applicationService.hasCompany(companyId)) {
      this.applicationService.setPropertyFilter({
        propertyId: null,
        companyId,
        propertyTypeId: null
      });
    } else {
      this.applicationService.setPropertyFilter();
    }
  }

  public updateHasProjects(): void {
    const res = this.applicationService.updateHasProjects();
    this.hasProjects = res;
  }

  public get homePageUrl(): Array<string> {
    return [this.baseUrlService.getHomeBaseURL()];
  }

  public toggleAiAssistant(): void {
    this.showAiAssistant = !this.showAiAssistant;
  }
}
